import type { FC } from 'react';
import React from 'react';
import {
  Badge,
  CandidateProfileUtil,
  CondensedSmall,
  DateUtil,
  Grid,
  GridColumn,
  Gutter,
  Icon,
  ImageCircle,
  Layout,
  LayoutItem,
  ProfileStatusBadge,
  SmallHeader,
  Tooltip,
} from '@axiom/ui';
import type { Candidate, User } from '@axiom/validation';
import { DisplayPronouns } from '@axiom/const';

import {
  CandidateUtil,
  PROACTIVE_BADGE_CONFIG,
} from '../../utils/candidate-util';

export interface CandidateAttributesProps {
  children?: React.ReactNode;
  candidate: Candidate;
  usersById: Record<User['id'], User['fullName']>;
  showAlertIcon?: boolean;
  inlineBadges?: boolean;
  name?: string;
}

export const CandidateAttributes: FC<CandidateAttributesProps> = ({
  children,
  candidate,
  usersById,
  showAlertIcon,
  inlineBadges = false,
  name = 'candidates-attributes-wrapper',
}) => {
  const proactiveBadgeStatus = CandidateUtil.getProactiveBadgeStatus(
    candidate.profileStatus,
    candidate.isProfileShared
  );

  const nameBlock = (
    <Layout position="middle">
      <LayoutItem rightGutter="16px">
        <ImageCircle
          name="candidate-image"
          size="default"
          src={CandidateProfileUtil.getProfileImageUri(
            { profileImageKey: candidate.profileImageKey },
            'W_100'
          )}
          imageName={candidate.calculatedDisplayName}
        />
      </LayoutItem>
      <LayoutItem rightGutter="24px">
        <SmallHeader maxLines={2} name="candidate-name">
          {candidate.calculatedDisplayName}
          {showAlertIcon ? (
            <>
              {' '}
              <Icon name="bell" />
            </>
          ) : null}
        </SmallHeader>
        {candidate.pronouns ? (
          <CondensedSmall name="candidate-pronoun">
            {DisplayPronouns[candidate.pronouns]}
          </CondensedSmall>
        ) : null}
      </LayoutItem>
    </Layout>
  );

  const badges = (
    <Layout position="middle" horizontalGutter="8px" verticalGutter="8px" wrap>
      {candidate.profileStatus ? (
        <ProfileStatusBadge
          profileStatus={candidate.profileStatus}
          name={`${candidate.id}-profile-status-badge`}
        />
      ) : null}
      {candidate.isPriorityTalent ? (
        <Badge
          background="forest green"
          name={`${candidate.id}-priority-badge`}
        >
          Priority
        </Badge>
      ) : null}
      {candidate.isFlagged ? (
        <Badge background="red" name={`${candidate.id}-flagged-badge`}>
          Flagged
        </Badge>
      ) : null}
      <Badge
        background={PROACTIVE_BADGE_CONFIG[proactiveBadgeStatus].background}
        name={`${candidate.id}-proactive-${proactiveBadgeStatus}-badge`}
      >
        {PROACTIVE_BADGE_CONFIG[proactiveBadgeStatus].label}
      </Badge>
      {candidate.isFlagged ? (
        <Tooltip name="flagged-candidate-tooltip">
          <div>Please reach out to the Talent Owner before proceeding.</div>
          {candidate.flaggedBy ? (
            <div>
              Flagged by: {usersById[candidate.flaggedBy]}{' '}
              <div>{DateUtil.displayTimestamp(candidate.flaggedAt)}</div>
            </div>
          ) : null}
        </Tooltip>
      ) : null}
    </Layout>
  );

  const statusLastEdited = (
    <CondensedSmall name="status-last-edited">
      Status last edited:{' '}
      {DateUtil.displayTimestamp(candidate.profileStatusUpdatedAt)} -{' '}
      {CandidateUtil.getProfileStatusEditorName(candidate, usersById)}
    </CondensedSmall>
  );

  if (inlineBadges) {
    return (
      <Layout position="space-between middle" name={name}>
        <LayoutItem hug>
          <Grid>
            <GridColumn width={7}>{nameBlock}</GridColumn>
            <GridColumn width={5} verticalAlign="middle">
              <Layout direction="vertical" verticalGutter="4px">
                <LayoutItem>{badges}</LayoutItem>
                <LayoutItem>{statusLastEdited}</LayoutItem>
              </Layout>
            </GridColumn>
          </Grid>
        </LayoutItem>
        <LayoutItem>{children}</LayoutItem>
      </Layout>
    );
  }

  return (
    <Layout direction="vertical" name={name}>
      <LayoutItem>
        <Layout position="space-between middle">
          <LayoutItem>{nameBlock}</LayoutItem>
          <LayoutItem>{children}</LayoutItem>
        </Layout>
      </LayoutItem>
      <LayoutItem>
        <Gutter vertical="8px">{badges}</Gutter>
      </LayoutItem>
      <LayoutItem>{statusLastEdited}</LayoutItem>
    </Layout>
  );
};
